/**
 * HealthFooter — Operational Health + Business Brain Health strip.
 *
 * Derives the two counts by *filtering* the buckets returned by the
 * backend. This is not client-side inference: it is a partitioning of
 * the canonical response by module name.
 *
 *   Operational Health   ← findings from module ``operations_sentinel``
 *   Business Brain Health ← findings from module ``knowledge_curator``
 */
import type { AkinexBriefing, AkinexFinding } from "@/entities/akinex/api";
import { flattenBriefingFindings } from "@/entities/akinex/api";

function countByModule(
  briefing: AkinexBriefing,
  moduleName: string,
): number {
  return flattenBriefingFindings(briefing).filter(
    (f: AkinexFinding) => f.module === moduleName,
  ).length;
}

function moduleStatus(
  briefing: AkinexBriefing,
  moduleName: string,
): "active" | "inactive" {
  const catalog = briefing.module_catalog ?? [];
  const entry = catalog.find((m) => m.name === moduleName);
  return entry?.status ?? "inactive";
}

function healthLabel(count: number, active: boolean): {
  label: string;
  tone: "healthy" | "attention" | "inactive";
} {
  if (!active) return { label: "Standing by", tone: "inactive" };
  if (count === 0) return { label: "All clear", tone: "healthy" };
  return {
    label: `${count} item${count === 1 ? "" : "s"} to address`,
    tone: "attention",
  };
}

const TONE_CLASS: Record<"healthy" | "attention" | "inactive", string> = {
  healthy: "text-emerald-400",
  attention: "text-amber-400",
  inactive: "text-[var(--md-fog)]",
};

export interface HealthFooterProps {
  briefing: AkinexBriefing;
}

export function HealthFooter({ briefing }: HealthFooterProps): JSX.Element {
  const opsCount = countByModule(briefing, "operations_sentinel");
  const brainCount = countByModule(briefing, "knowledge_curator");
  const opsActive = moduleStatus(briefing, "operations_sentinel") === "active";
  const brainActive = moduleStatus(briefing, "knowledge_curator") === "active";

  const ops = healthLabel(opsCount, opsActive);
  const brain = healthLabel(brainCount, brainActive);

  return (
    <div
      data-testid="akinex-health-footer"
      className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-white/5 pt-4"
    >
      <div data-testid="akinex-health-operational">
        <div className="text-[11px] uppercase tracking-widest text-[var(--md-fog)]">
          Operational Health
        </div>
        <div
          className={`mt-1 text-sm font-medium ${TONE_CLASS[ops.tone]}`}
          data-testid="akinex-health-operational-label"
        >
          {ops.label}
        </div>
      </div>
      <div data-testid="akinex-health-brain">
        <div className="text-[11px] uppercase tracking-widest text-[var(--md-fog)]">
          Business Brain Health
        </div>
        <div
          className={`mt-1 text-sm font-medium ${TONE_CLASS[brain.tone]}`}
          data-testid="akinex-health-brain-label"
        >
          {brain.label}
        </div>
      </div>
    </div>
  );
}
